import { useMemo } from "react";
import { useIsMutating, useMutation, useQuery } from "@tanstack/react-query";
import { getQueryClient } from "./cacheHelpers";
import { get, getQueryKey, getUrl } from "./cacheHelpers";
import { useToken } from "./MFEProvider";
const getUpdateRequest = (baseUrl, method, token, hookOptions, updateOptions) => window.fetch(updateOptions?.url || baseUrl, {
    method,
    headers: {
        ...(hookOptions?.useToken ? { Authorization: `Bearer ${token}` } : {}),
        ...hookOptions?.headers,
        ...updateOptions?.headers,
    },
    body: updateOptions?.body ?? null,
});
export const useRemoteData = (url, options) => {
    const parseData = options && "parse" in options ? options.parse : undefined;
    const queryClient = getQueryClient();
    const { token } = useToken();
    const urlObject = getUrl(url, options?.queryParams);
    const queryKey = getQueryKey(urlObject, options?.ignoreParams);
    const query = useQuery(queryKey, () => get(urlObject.href, token, options), {
        staleTime: Infinity,
        ...options,
        ...(parseData ? { select: parseData } : {}),
    });
    // using useMutation allows every instance of the hook to share update state, like `isLoading`
    const mutation = useMutation({
        mutationKey: queryKey,
        mutationFn: async (updater) => {
            const previousCacheData = queryClient.getQueryData(queryKey);
            const setCache = (newData) => {
                try {
                    if (parseData) {
                        // Test if the data can be parsed
                        parseData(newData);
                    }
                    queryClient.setQueryData(queryKey, newData);
                }
                catch (e) {
                    console.error(e);
                    queryClient.setQueryData(queryKey, previousCacheData);
                }
            };
            const returnedData = await updater(previousCacheData, setCache, {
                url: urlObject,
                put: (updateOptions) => getUpdateRequest(url, "PUT", token, options, updateOptions),
                post: (updateOptions) => getUpdateRequest(url, "POST", token, options, updateOptions),
                delete: (updateOptions) => getUpdateRequest(url, "DELETE", token, options, updateOptions),
            });
            if (returnedData) {
                setCache(returnedData);
            }
            const finalCachedData = returnedData ?? queryClient.getQueryData(queryKey);
            return parseData ? parseData(finalCachedData) : finalCachedData;
        },
    });
    const isUpdating = useIsMutating({ mutationKey: queryKey });
    return useMemo(() => ({
        mapResult: ({ onData, onLoading, onError, onInit }) => {
            if (query.status === "success")
                return onData(query.data, query.isRefetching);
            if (query.isInitialLoading)
                return onLoading();
            if (query.error)
                return onError(query.error);
            if (onInit)
                return onInit();
            return onLoading();
        },
        refresh: async (refreshOptions) => {
            await queryClient.fetchQuery({
                queryKey,
                queryFn: () => get(urlObject.href, token, { ...options, ...refreshOptions }),
                ...options,
                ...refreshOptions,
            });
        },
        updateCache: (updater) => mutation.mutateAsync(updater),
        isUpdating: () => Boolean(isUpdating),
        getState: () => {
            switch (query.status) {
                case "success":
                    return {
                        status: query.status,
                        data: query.data,
                        isRefetching: query.isRefetching,
                    };
                case "error":
                    return {
                        status: query.status,
                        error: query.error,
                    };
                case "loading":
                default:
                    return {
                        status: query.status,
                    };
            }
        },
    }), [query, mutation, isUpdating]);
};
